// hooks/useBootState.ts
// Assembles BootInputs from the Auth + AppData contexts and returns the
// derived BootState. In the shield architecture this is consumed by BootShield
// only (state === 'booting' keeps the splash up); it does NOT drive routing.
import { useAuthContext } from '../contexts/AuthContext';
import { useAppData } from '../contexts/AppDataContext';
import { safeLocalStorage } from '../utils/safeStorage';
import { deriveBootState, type BootState, type BootInputs } from './bootState';
import { isDataReady } from './participantScreen';

export interface BootResult {
  state: BootState;
  inputs: BootInputs;
}

export function useBootState(): BootResult {
  const { user, claimsRole, authResolved, claimsKnown, profileSettled } = useAuthContext();
  const { appData, isLoadingData, currentProjectId } = useAppData();

  const savedId = currentProjectId || safeLocalStorage.getItem('dekko_current_project_id') || '';
  // Shared with ParticipantNewPage's oracle. Duplicating this formula here is
  // what produced the "role-less user waits forever for appData that never
  // arrives" bug when it was copied to the participant call site.
  const dataReady = isDataReady(isLoadingData, appData !== null, claimsRole);
  // dataReady is TRUE for a role-less user with appData still null (see
  // isDataReady), so this must NOT assert appData — with a leftover
  // dekko_current_project_id in localStorage that deref crashed boot render
  // and pinned every load on the splash (2026-08-04, all platforms).
  const savedProjectValid = dataReady && !!savedId && !!appData &&
    appData.projects.some(p => p.id === savedId);

  const inputs: BootInputs = {
    authResolved,
    hasUser: !!user,
    claimsKnown,
    claimsRole,
    profileSettled,
    dataReady,
    savedProjectValid,
    pendingJoin: !!safeLocalStorage.getItem('pendingProjectJoin'),
  };

  return { state: deriveBootState(inputs), inputs };
}
